import { Transition } from "@headlessui/react";
import { CRMChannel } from "@jugl-web/rest-api";
import { Tab } from "@jugl-web/ui-components/cross-platform";
import { cx, useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, useEffect, useMemo, useState } from "react";
import { PlainButton, Tooltip } from "@jugl-web/ui-kit";
import { useClientSupportPageContext } from "../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { CustomerTabContent } from "./components/CustomerTabContent";
import { TasksTabContent } from "./components/TasksTabContent";
import { TicketsTabContent } from "./components/TicketsTabContent";
import { ConversationSidePanelTab } from "./types";
import { collapsedIconButtonClassName } from "../../consts";
import { ReactComponent as CloseIcon } from "./assets/close.svg";

export const ConversationSidePanel: FC = () => {
  const { t } = useTranslations();
  const { isCx } = useAppVariant();
  const {
    conversationSidePanelVariant,
    openConversationSidePanel,
    selectedChannel,
    closeConversationSidePanel,
  } = useClientSupportPageContext();

  const isWhatsappChannel = selectedChannel === CRMChannel.whatsapp;
  const showTasksTab = !isCx && isWhatsappChannel;
  const showTicketsTab = isCx;

  const [isPanelVisible, setIsPanelVisible] = useState(false);

  useEffect(() => {
    setIsPanelVisible(true);
  }, []);

  const selectedTab =
    conversationSidePanelVariant ?? ConversationSidePanelTab.customer;

  const tabs = useMemo(
    () =>
      [
        {
          id: ConversationSidePanelTab.customer,
          title: t({
            id: "client-support-page.customer",
            defaultMessage: "Customer",
          }),
          onClick: () =>
            openConversationSidePanel({
              variant: ConversationSidePanelTab.customer,
            }),
        },
        showTicketsTab && {
          id: ConversationSidePanelTab.tickets,
          title: t({
            id: "client-support-page.tickets",
            defaultMessage: "Tickets",
          }),
          onClick: () =>
            openConversationSidePanel({
              variant: ConversationSidePanelTab.tickets,
            }),
        },
        showTasksTab && {
          id: ConversationSidePanelTab.tasks,
          title: t({
            id: "common.tasks",
            defaultMessage: "Tasks",
          }),
          onClick: () =>
            openConversationSidePanel({
              variant: ConversationSidePanelTab.tasks,
            }),
        },
      ].filter(Boolean) as Array<{
        id: ConversationSidePanelTab;
        title: string;
        onClick: () => void;
      }>,
    [openConversationSidePanel, showTasksTab, showTicketsTab, t]
  );

  const activeTab = useMemo(() => {
    if (selectedTab === ConversationSidePanelTab.tasks && !showTasksTab) {
      return ConversationSidePanelTab.customer;
    }
    if (selectedTab === ConversationSidePanelTab.tickets && !showTicketsTab) {
      return ConversationSidePanelTab.customer;
    }
    if (!isCx && selectedTab === ConversationSidePanelTab.tickets) {
      return ConversationSidePanelTab.customer;
    }
    return selectedTab;
  }, [isCx, selectedTab, showTasksTab, showTicketsTab]);

  return (
    <Transition
      as="div"
      show={isPanelVisible}
      enter="transition-[width] duration-200 ease-out"
      enterFrom="w-0"
      enterTo="w-[420px]"
      className="h-full shrink-0 overflow-hidden"
    >
      <div className="flex h-full w-[420px] flex-col border-0 border-l border-solid border-dark-100 bg-white">
        <div className="flex h-[60px] shrink-0 items-center justify-between gap-8 border-0 border-b border-solid border-dark-100 px-6">
          <div className="flex h-full items-end">
            {tabs.map((tab) => (
              <Tab
                key={tab.id}
                title={tab.title}
                isSelected={tab.id === activeTab}
                onClick={tab.onClick}
                className="w-auto"
                labelClassName="text-sm"
              />
            ))}
          </div>
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                ref={ref}
                className={cx(collapsedIconButtonClassName, "h-8 w-8")}
                {...props}
                onClick={closeConversationSidePanel}
              >
                <CloseIcon />
              </PlainButton>
            )}
          >
            {t({
              id: "client-support-page.close-info-panel",
              defaultMessage: "Close Info Panel",
            })}
          </Tooltip>
        </div>
        <div className="jugl__custom-scrollbar min-h-0 flex-1 overflow-auto">
          {activeTab === ConversationSidePanelTab.customer && (
            <CustomerTabContent />
          )}
          {activeTab === ConversationSidePanelTab.tickets && showTicketsTab && (
            <TicketsTabContent />
          )}
          {activeTab === ConversationSidePanelTab.tasks && showTasksTab && (
            <TasksTabContent />
          )}
        </div>
      </div>
    </Transition>
  );
};
